import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { WorkingDaysEditor, formatWorkingDays } from './WorkingDaysEditor';
import { EmploymentTypeSelector, EmploymentTypeBadge } from '@/components/EmploymentTypeSelector';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Briefcase } from 'lucide-react';

/**
 * Dialog for editing a user's working days pattern and employment type from Team Overview
 *
 * @param {Object} props
 * @param {Object} props.member - Team member object with id, first_name, last_name, working_days, employment_type_id
 * @param {number[]} props.companyDefault - Company's default working days
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSaved - Callback after successful save
 */
export function EditWorkingDaysDialog({
  member,
  companyDefault = [1, 2, 3, 4, 5],
  open,
  onOpenChange,
  onSaved
}) {
  const [useCompanyDefault, setUseCompanyDefault] = useState(true);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [employmentTypeId, setEmploymentTypeId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset state when member changes or dialog opens
  useEffect(() => {
    if (member && open) {
      const hasOverride = member.working_days !== null && member.working_days !== undefined;
      setUseCompanyDefault(!hasOverride);
      setWorkingDays(hasOverride ? member.working_days : companyDefault);
      setEmploymentTypeId(member.employment_type_id || null);
    }
  }, [member, companyDefault, open]);

  const handleSave = async () => {
    if (!member?.id) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('company_members')
        .update({
          working_days: useCompanyDefault ? null : workingDays,
          employment_type_id: employmentTypeId,
          updated_date: new Date().toISOString()
        })
        .eq('user_id', member.id);

      if (error) throw error;

      toast.success(`Settings updated for ${member.first_name} ${member.last_name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating member settings:', err);
      toast.error('Failed to update settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDefault = (useDefault) => {
    setUseCompanyDefault(useDefault);
    if (!useDefault && workingDays.length === 0) {
      // If switching to custom and no days selected, use company default as starting point
      setWorkingDays([...companyDefault]);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Member Settings</DialogTitle>
          <DialogDescription>
            Configure attendance settings for {member.first_name} {member.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Employment Type Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-slate-500" />
              <Label className="text-sm font-medium">Employment Type</Label>
            </div>
            <EmploymentTypeSelector
              value={employmentTypeId}
              onChange={setEmploymentTypeId}
              placeholder="Select employment type"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Determines leave entitlements, bank holiday eligibility, and other attendance rules
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200" />

          {/* Working Days Section */}
          <div>
            <WorkingDaysEditor
              value={workingDays}
              onChange={setWorkingDays}
              companyDefault={companyDefault}
              useCompanyDefault={useCompanyDefault}
              onToggleDefault={handleToggleDefault}
            />

            {/* Summary info */}
            <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm">
              <p className="font-medium mb-1">
                {useCompanyDefault ? 'Using company default' : 'Custom pattern'}
              </p>
              <p className="text-muted-foreground">
                Working days: {formatWorkingDays(useCompanyDefault ? companyDefault : workingDays)}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
